import { Controller } from '@hotwired/stimulus'

/**
 * Shared accordion controller for FAQ and Pricing FAQ sections
 * Supports keyboard navigation and ARIA attributes for accessibility
 *
 * Usage:
 *   <div data-controller="public--accordion">
 *     <div data-public--accordion-target="item">
 *       <button data-action="click->public--accordion#toggle keydown->public--accordion#handleKeydown"
 *               data-public--accordion-target="trigger"
 *               aria-expanded="false">
 *         Question text
 *       </button>
 *       <div data-public--accordion-target="content" class="hidden">
 *         Answer content
 *       </div>
 *     </div>
 *   </div>
 */
// Counter for generating unique accordion instance IDs
let accordionInstanceCounter = 0

export default class extends Controller {
  static targets = ['item', 'trigger', 'content']
  static values = {
    allowMultiple: { type: Boolean, default: false },
    trackEvents: { type: Boolean, default: true },
    category: { type: String, default: '' }
  }

  connect() {
    // Generate a unique instance ID for this accordion
    this.instanceId = this.element.id || `accordion-${++accordionInstanceCounter}`

    // Initialize ARIA attributes with unique IDs per item
    this.triggerTargets.forEach((trigger, index) => {
      const content = this.contentTargets[index]
      const triggerId = `${this.instanceId}-trigger-${index}`
      const contentId = `${this.instanceId}-content-${index}`
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

      trigger.setAttribute('id', triggerId)
      trigger.setAttribute('aria-controls', contentId)
      content.setAttribute('id', contentId)
      content.setAttribute('aria-labelledby', triggerId)
      content.setAttribute('role', 'region')
      // Set initial aria-hidden based on expanded state
      content.setAttribute('aria-hidden', isExpanded ? 'false' : 'true')
    })
  }

  toggle(event) {
    const trigger = event.currentTarget
    const index = this.triggerTargets.indexOf(trigger)
    const content = this.contentTargets[index]
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

    if (!this.allowMultipleValue && !isExpanded) {
      // Close other items first
      this.closeAll()
    }

    if (isExpanded) {
      this.close(trigger, content)
    } else {
      this.open(trigger, content)
      this.trackOpen(trigger, index)
    }
  }

  open(trigger, content) {
    trigger.setAttribute('aria-expanded', 'true')
    content.setAttribute('aria-hidden', 'false')
    content.classList.remove('hidden')
    content.style.maxHeight = content.scrollHeight + 'px'

    // Add open class for styling
    trigger.closest('[data-public--accordion-target="item"]')?.classList.add('is-open')
  }

  close(trigger, content) {
    trigger.setAttribute('aria-expanded', 'false')
    content.setAttribute('aria-hidden', 'true')
    content.style.maxHeight = '0px'

    // Remove open class
    trigger.closest('[data-public--accordion-target="item"]')?.classList.remove('is-open')

    // Hide after animation
    setTimeout(() => {
      if (trigger.getAttribute('aria-expanded') === 'false') {
        content.classList.add('hidden')
      }
    }, 300)
  }

  closeAll() {
    this.triggerTargets.forEach((trigger, index) => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.close(trigger, this.contentTargets[index])
      }
    })
  }

  handleKeydown(event) {
    const trigger = event.currentTarget
    const index = this.triggerTargets.indexOf(trigger)

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.focusNext(index)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.focusPrevious(index)
        break
      case 'Home':
        event.preventDefault()
        this.triggerTargets[0]?.focus()
        break
      case 'End':
        event.preventDefault()
        this.triggerTargets[this.triggerTargets.length - 1]?.focus()
        break
    }
  }

  focusNext(currentIndex) {
    const nextIndex = (currentIndex + 1) % this.triggerTargets.length
    this.triggerTargets[nextIndex]?.focus()
  }

  focusPrevious(currentIndex) {
    const prevIndex = currentIndex === 0 ? this.triggerTargets.length - 1 : currentIndex - 1
    this.triggerTargets[prevIndex]?.focus()
  }

  trackOpen(trigger, index) {
    if (!this.trackEventsValue) return

    const questionText = trigger.textContent?.trim()?.substring(0, 100)

    if (typeof ahoy !== 'undefined') {
      ahoy.track('faq_opened', {
        question: questionText,
        index: index,
        category: this.categoryValue || 'general',
        page: window.location.pathname
      })
    }
  }
}
